<script>
export default {
	name: 'BookingWidget',
	editConfig: {
		emptyLabel: 'Booking Widget',
		isEmpty (props) {
			return props.desktopProperties.length === 0;
		},
	},
	inheritAttrs: false,
};
</script>

<script setup>
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import SwitchTab from '~/aem-components/SwitchTab.vue';
import FontAwesomeLazy from '~/components/FontAwesomeLazy.vue';
import VueSwiper from '~/components/VueSwiper.vue';
import { getCompositeComponent } from '@custom/aem-vue-editable-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { isMobileViewport } from '~/logic/composables/breakpoints.js';
import { sleep } from '~/logic/helpers/utils.js';
import { useEventBus } from '@vueuse/core';
import { pushAA_click } from '~/logic/adobe-analytic.js';
import { useRouter } from 'vue-router';

import CheckIn from '~/aem-components/booking/CheckIn.vue';
import FlightHotelSearch from '~/aem-components/booking/FlightHotelSearch.vue';
import FlightSchedule from '~/aem-components/booking/FlightSchedule.vue';
import FlightSearch from '~/aem-components/booking/FlightSearch.vue';
import FlightStatus from '~/aem-components/booking/FlightStatus.vue';
import ManageBooking from '~/aem-components/booking/ManageBooking.vue';
import MHupgrade from '~/aem-components/booking/MHupgrade.vue';
import MHEFlightSearch from '~/aem-components/mhe/BookingWidget/MHEFlightSearch.vue';
import MobileSubpage from '~/components/BookingWidget/MobileSubpage.vue';

const props = defineProps({
	desktopProperties: { type: Array, default: () => [] },
	mobileProperties: { type: Array, default: () => [] },
	componentIdAA: { type: String, default: '' },
	togglePointsApiURL: { type: String, default: '' },
	enableGlassDesign: { type: Boolean, default: false },
});
const emit = defineEmits([
	// 'update:modelValue',
]);

const rootEl = ref(null);
const switchTabEl = ref(null);
const switchTabMblEl = ref(null);
const isFromDestTextClicked = ref(false);

const FLIGHT_SEARCH_ID = 'flight-search-tab';

const TAB_CONTENT_MAPPING = {
	'flight-search': {
		component: FlightSearch,
		tabTitle: 'Flight Search',
		anchorTag: FLIGHT_SEARCH_ID,
		selectedDefault: false,
		parsys: {
			type: 'mh/components/booking/flight-search',
			componentKey: 'flight-search',
		},
		mblBtmBarUrl: '#flight-search',
	},
	'flight-hotel': {
		component: FlightHotelSearch,
		tabTitle: 'Flight + Hotel',
		anchorTag: 'flight-hotel-search-tab',
		selectedDefault: false,
		parsys: {
			type: 'mh/components/booking/flight-hotel-search',
			componentKey: 'flight-hotel-search',
		},
		mblBtmBarUrl: '#flight-hotel-search',
	},
	'manage-booking': {
		component: ManageBooking,
		tabTitle: 'Manage Booking',
		anchorTag: 'manage-booking-tab',
		selectedDefault: false,
		parsys: {
			type: 'mh/components/booking/manage-booking',
			componentKey: 'manage-booking',
		},
		mblBtmBarUrl: '#manage-booking',
	},
	'check-in': {
		component: CheckIn,
		tabTitle: 'Check In',
		anchorTag: 'check-in-tab',
		selectedDefault: false,
		parsys: {
			type: 'mh/components/booking/check-in',
			componentKey: 'check-in',
		},
		mblBtmBarUrl: '#check-in',
	},
	'flight-status': {
		component: FlightStatus,
		tabTitle: 'Flight Status',
		anchorTag: 'flight-status-tab',
		selectedDefault: false,
		parsys: {
			type: 'mh/components/booking/flight-status',
			componentKey: 'flight-status',
		},
		mblBtmBarUrl: '#flight-status',
	},
	'flight-schedule': {
		component: FlightSchedule,
		tabTitle: 'Flight Schedule',
		anchorTag: 'flight-schedule-tab',
		selectedDefault: false,
		parsys: {
			type: 'mh/components/booking/flight-schedule',
			componentKey: 'flight-schedule',
		},
		mblBtmBarUrl: '#flight-schedule',
	},
	'mh-upgrade': {
		component: MHupgrade,
		tabTitle: 'MHupgrade',
		anchorTag: 'mh-upgrade-tab',
		selectedDefault: false,
		parsys: {
			type: 'mh/components/booking/mh-upgrade',
			componentKey: 'mh-upgrade',
		},
		mblBtmBarUrl: '#mh-upgrade',
	},
	'mhe-flight-search': {
		component: MHEFlightSearch,
		tabTitle: 'MHE Flight Search',
		anchorTag: 'mhe-flight-search-tab',
		selectedDefault: false,
		parsys: {
			type: 'mh/components/mhexplorer/booking/mhe-flight-search',
			componentKey: 'mhe-flight-search',
		},
		mblBtmBarUrl: null,
	},
};


const tabList = computed(() => {
	return props.desktopProperties.map((item) => {
		if (item.isHideInDesktop) return null;
		const mapped = TAB_CONTENT_MAPPING[item.function];
		if (!mapped) {
			console.warn(`Couldn't find correct mapping data for `, item);
			return null;
		}
		return {
			...mapped,
			tabTitle: item.ctaText,
			composite: getCompositeComponent(mapped.parsys),
		};
	}).filter(Boolean);
});

const mobileListItem = computed(() => {
	return props.mobileProperties.map((item) => {
		if (item.isHideInMobile) return null;
		return {
			...item,
		};
	}).filter(Boolean);
});

const activeTabName = ref(tabList.value?.[0].anchorTag);
const mblActiveTabName = ref(tabList.value?.[0].tabTitle);
const mblActiveTabNameAnchor = ref(tabList.value?.[0].anchorTag);
const activeSubTabName = ref('book-flight');
const isMblSubPgVisibleForFlightSearch = ref(false);

// we need a flag so that we can set class, to apply custom styling when swiper is not engaged (i.e. locked)
// basically, we want to center the slides
const isMobileSwiperLocked = ref(false);


const { isEditorMode, bookingWidgetSubTab } = useGlobalAEMState();

const hasFlightSearchAsChild = computed(() => {
	return !!tabList.value.find((item) => item.anchorTag === FLIGHT_SEARCH_ID);
});


let scrollTriggerInstance = null;

const bookingWidgetBus = useEventBus('booking-widget');

bookingWidgetBus.on((event, payload) => {
	switch (event) {
		case 'refresh-scroll-trigger': {
			scrollTriggerInstance?.refresh();
			break;
		}
		case 'booking-widget-ond-selected': {
			// isMblSubPgVisibleForFlightSearch.value = true;
			router.replace({ hash: '#flight-search' });
			// handleSwitchTabUpdate('flight-search-tab', 'anchor');
			break;
		}
		case 'mobilt-btm-bar': {
			handleSwitchTabUpdate(payload?.urlPath, 'mblBtmBarUrl');
			break;
		}
		case 'sub-tab-selected': {
			router.replace({ hash: '#flight-search' });
			break;
		}
		case 'close-from-mobile': {
			mblActiveTabName.value = tabList.value?.[0].tabTitle;
			activeSubTabName.value = 'book-flight';
			updateView(false);
			break;
		}
		default: { break; }
	}
});

const handleStickyElMounted = async () => {
	// return;
	if (isEditorMode.value) return;
	if (!hasFlightSearchAsChild.value) return;
	
	gsap.registerPlugin(ScrollTrigger);
	await sleep(1500);
	
	const triggerEl = switchTabEl.value?.rootEl?.querySelector('.SwitchTabContent');
	const triggerMblEl = switchTabMblEl.value?.rootEl?.querySelector('.SwitchTabContent');

	let lastFocusedEl = document.activeElement;
	
	const resizeObserver = new ResizeObserver((entries) => {
		lastFocusedEl = document.activeElement;
		
		for (const entry of entries) {
			const borderBoxSize = entry.borderBoxSize[0];
			const height = borderBoxSize.blockSize;
			scrollTriggerInstance?.refresh();
			st2?.refresh();
			rootEl.value?.style.setProperty('--pinSpacingHeight', `${triggerEl.offsetHeight}px`);
			// console.log('resizeObserver - triggerEl size changed, height = ', height);
		}
		
		nextTick().then(() => {
			if (lastFocusedEl && (lastFocusedEl !== document.body)) lastFocusedEl.focus();
		});
	});

	resizeObserver.observe(triggerEl);
	
	scrollTriggerInstance = ScrollTrigger.create({
		trigger: triggerEl,
		// start: `+=94 top`, // 94 = pixel from top of '.SwitchTabContent' that it should start sticking
		start: `bottom top`, // 94 = pixel from top of '.SwitchTabContent' that it should start sticking
		toggleClass: 'sticked',
		endTrigger: document.body,
		pin: true,
		pinSpacing: false,
		onToggle: (instance) => {
			if (instance.isActive) {
				bookingWidgetBus.emit('sticky:sticked', { rootEl, triggerEl, scrollTriggerInstance, resizeObserver });
			} else {
				bookingWidgetBus.emit('sticky:unsticked', { rootEl, triggerEl, scrollTriggerInstance, resizeObserver });
			}
		},
		// markers: { startColor: 'green', endColor: 'green' },
	});
	
	// we need this to add "pre-stick" class to triggerEl, to hide it to prevent flash of expanded pinned booking widget
	let st2 = ScrollTrigger.create({
		trigger: triggerEl,
		start: `92.5% top`,
		endTrigger: document.body,
		onToggle: (instance) => {
			if (instance.isActive) {
				triggerEl.classList.add('pre-stick');
			} else {
				triggerEl.classList.remove('pre-stick');
			}
		},
		// markers: { startColor: 'purple', endColor: 'purple' },
	});
	
	// window.scrollTriggerInstance = scrollTriggerInstance;
	
	const homepageTransitionInBus = useEventBus('homepage-transition-in');
	homepageTransitionInBus.on((event, payload) => {
		switch (event) {
			case 'animation-complete': {
				rootEl.value.style.setProperty('--pinSpacingHeight', `${triggerEl.offsetHeight}px`);
				rootEl.value.style.setProperty('--pinSpacingHeight', `${triggerMblEl?.offsetHeight}px`);
				st2?.refresh();
				scrollTriggerInstance.refresh();
				break;
			}
			default: {
				// console.log(`Unknown event: "${event}". Ignoring it.`);
				break;
			}
		}
	});
	
	
	if (isMobileViewport.value) {
		scrollTriggerInstance.disable();
	} else {
		// temporary HACK to make sure sticky is correct
		/* let updateCount = 0;
		const MAX_UPDATE_COUNT = 3;
		let intervalId = setInterval(() => {
			scrollTriggerInstance.refresh();
			updateCount++;
			if (updateCount >= MAX_UPDATE_COUNT) clearInterval(intervalId);
		}, 700); */
	}
};

const handleStickyElBeforeUnmount = async () => {
	scrollTriggerInstance.kill();
};

watch(isMobileViewport, (newValue, oldValue) => {
	if (newValue) {
		scrollTriggerInstance?.disable();
	} else {
		scrollTriggerInstance?.enable();
	}
});

const sendTabUpdateAA = (newTabName) => {
	pushAA_click([
		{
			clickName: newTabName,
			clickComponentType: 'URL',
			componentName: 'BookingWidget',
			componentID: props.componentIdAA,
		},
		{
			name: newTabName,
			type: 'other',
		},
	]);
};
const isSyncing = ref(false);
const router = useRouter();
const handleSwitchTabUpdate = async (newTabName, filterBy) => {
	// sendTabUpdateAA(newTabName);
	// for revamp 2 version mbl view
	if (isSyncing.value) return;
	let tab = null;
	if (filterBy === 'anchor') {
		tab = Object.values(TAB_CONTENT_MAPPING).find(
			(item) => item.anchorTag === newTabName
		);
	}
	if (filterBy === 'mblBtmBarUrl') {
		tab = Object.values(TAB_CONTENT_MAPPING).find(
			(item) => item.mblBtmBarUrl === newTabName
		);
	}
	
	mblActiveTabName.value = tab ? tab.tabTitle : null;
	mblActiveTabNameAnchor.value = tab ? tab.anchorTag : null;

	if (isMobileViewport.value && props.enableGlassDesign ) {
		// isMblSubPgVisible.value = true;

		await nextTick(); // 👈 critical
	}
	isSyncing.value = true;
	// switchTabMblEl.value?.switchTab(mblActiveTabNameAnchor.value);
	// small delay to allow event cycle to finish
	setTimeout(() => {
		isSyncing.value = false;
	}, 0);
};

const handleSubTabUpdate = (newTabName) => {

	activeSubTabName.value = newTabName;
	
	// deactivating below line of code to fix : #47793
	// sendTabUpdateAA(newTabName);
};

const globalBookFlightBus = useEventBus('booking-widget:book-flight');
globalBookFlightBus.on(async (event, payload) => {
	switch (event) {
		case 'active-tab-and-fill-up-form':
			if (tabList.value.length > 1) {
				const switchTabRef = switchTabEl.value;
				const switchTabMblRef = switchTabMblEl.value;

				switchTabRef?.switchTab(payload.mainTab);
				switchTabMblRef?.switchTab(payload.mainTab);
			}
			break;
		default:
			console.log(`Unknown event: ${event}. Ignore it.`);
			break;
	}
});

const updateView = async ($event) => {
	if (isSyncing.value) return;
	await nextTick(); // 👈 critical
	isSyncing.value = true;
	switchTabEl.value?.switchTab('flight-search-tab');
	activeTabName.value = 'flight-search-tab';
	// isMblSubPgVisibleForFlightSearch.value = $event;
	// small delay to allow event cycle to finish
	setTimeout(() => {
		isSyncing.value = false;
	}, 0);
};

onMounted(async () => {
	const swiper = document.querySelector('.BookingWidget .tab-swiper');
	if (!props.enableGlassDesign) {
		swiper.classList.add('md:hidden');
	}
	if (props.enableGlassDesign && isMobileViewport.value) {
		swiper.classList.add('text-sm', 'glass-in-mobile');
	}
});

</script>



<template>
<div
	ref="rootEl"
	class="BookingWidget relative z-50"
	:class="{
		'mt-0': tabList.length === 1,
		'md:-mt-3 mb-20': tabList.length !== 1,
		'glass-design -mt-16': props.enableGlassDesign,
		'-mt-22': !props.enableGlassDesign || isMobileViewport,
	}"
	:style="{
		'--tab-count': tabList.length,
	}"
>
	<SwitchTab
		ref="switchTabEl"
		class="tab-swiper"
		:tabList="tabList"
		:tabType="props.enableGlassDesign ? 'liquid-glass-tab' : 'mh-switch-tab'"
		:hideTabs="tabList.length === 1"
		:useSlotAsContent="true"
		:updateHashOnSwitch="false"
		:contentContainerClass="`rounded-6xl lg:rounded-2xl pt-8 pb-14 bg-white ${tabList.length === 1 ? '' : 'shadow-type-a'} ${props.enableGlassDesign && 'glass-design'}`"
		:desktopAllowTouchMove="true"
		componentNameAA="BookingWidget"
		:enableGlassDesign="props.enableGlassDesign"
		:dataVariant="props.enableGlassDesign ? 'booking-widget' : ''"
		@vue:mounted="handleStickyElMounted"
		@vue:before-unmount="handleStickyElBeforeUnmount"
		@update:active-tab="handleSwitchTabUpdate($event, 'anchor')"
	>
		<template
			v-for="tabItem in tabList"
			:key="tabItem.anchorTag"
			#[`content-${tabItem.anchorTag}`]
		>
			<div class="generic-container">
				<component
					:is="tabItem.composite.vNode"
					v-bind="tabItem.composite.vNodeProps"
					:isInBookingWidget="true"
					:togglePointsApiURL="props.togglePointsApiURL"
					:fromBookingWidget="true"
					@update:active-sub-tab="handleSubTabUpdate"
				></component>
			</div>
		</template>
	</SwitchTab>
	
	<!-- Mobile and not rafi glass design -->
	<div v-if="!props.enableGlassDesign" class=">md:hidden rounded-2xl pt-6 pb-4 shadow-type-a bg-white">
		<div class="">
			<VueSwiper
				class="mobile-swiper"
				:class="isMobileSwiperLocked ? 'is-locked' : ''"
				slidesPerView="auto"
				:spaceBetween="16"
				variant="slides-auto-width"
				:scrollbar="{
					draggable: true,
					hide: false,
				}"
				:observer="true"
				:observeParents="true"
				@lock="isMobileSwiperLocked = true"
				@unlock="isMobileSwiperLocked = false"
			>
				<AppHyperlink
					v-for="item in mobileListItem"
					:key="item.urlPath"
					class="mobile-item flex-col w-22"
					:href="item.urlPath"
				>
					<div class="rounded-full bg-primary-blue-base w-12 h-12 text-white">
						<FontAwesomeLazy
							v-if="item.iconType === 'icon-font-awesome-id'"
							class="mx-3 fill-current flex items-center h-full"
							:iconId="item.iconId"
							role="navigation"
							:aria-label="item.ctaText"
						/>
					</div>
					<span
						class="font-semibold typo-body-text-3 !leading-tight mt-2 text-$text-color text-center"
					>{{ item.ctaText }}</span>
				</AppHyperlink>
			</VueSwiper>
		</div>
		
	</div>
	<MobileSubpage
		:visiblityForFlightSearch="isMblSubPgVisibleForFlightSearch"
		class="text-white"
		style="--overlay-container-bg-color: var(--secondary-blue-extradark);"
		:enableGlassDesign="props.enableGlassDesign"
		mobileSubPageDesign="glass-design"
		@update:visiblity-for-flight-search="updateView($event)"
	>

		<template #banner-title>
			<h1 class="text-center">{{ $t(mblActiveTabName) }}</h1>
		</template>

		<template #default>
			<div class="generic-container my-4">
				<SwitchTab
					ref="switchTabMblEl"
					class="tab-swiper text-sm"
					:tabList="tabList"
					:tabType="props.enableGlassDesign ? 'liquid-glass-tab' : 'mh-switch-tab'"
					:hideTabs="tabList.length === 1"
					:useSlotAsContent="true"
					:updateHashOnSwitch="false"
					:contentContainerClass="`rounded-6xl lg:rounded-2xl pt-8 pb-14 bg-white ${tabList.length === 1 ? '' : 'shadow-type-a'} ${props.enableGlassDesign && 'glass-design'}`"
					:desktopAllowTouchMove="true"
					componentNameAA="BookingWidget"
					:enableGlassDesign="props.enableGlassDesign"
					dataVariant="booking-widget"
					@vue:mounted="handleStickyElMounted"
					@vue:before-unmount="handleStickyElBeforeUnmount"
					@update:active-tab="handleSwitchTabUpdate($event, 'anchor')"
				>
					<template
						v-for="tabItem in tabList"
						:key="tabItem.anchorTag"
						#[`content-${tabItem.anchorTag}`]
					>
						<div class="generic-container">
							<!-- <component :is="tabItem.component" /> -->
							<component
								:is="tabItem.composite.vNode"
								v-bind="tabItem.composite.vNodeProps"
								:isInBookingWidget="true"
								:togglePointsApiURL="props.togglePointsApiURL"
								@update:active-sub-tab="handleSubTabUpdate"
							></component>
						</div>
					</template>
				</SwitchTab>
			</div>
		</template>

	</MobileSubpage>

	
	
	<!-- This is a teleport target -->
	<div id="mobile-past-searches-container" class=">md:hidden"></div>
	
</div>
</template>


<style>
/* temporary only */
.gsap-marker-end {
	z-index: 10000 !important;
}
.gsap-marker-start {
	z-index: 10000 !important;
}
.gsap-marker-scroller-end {
	z-index: 10000 !important;
}
.gsap-marker-scroller-start {
	z-index: 10000 !important;
}
</style>


<style lang="scss">
@use '~/styles/partials/_var.scss';

.aem-AuthorLayer-Edit, .aem-AuthorLayer-structure {
	.BookingWidget {
		margin-top: 1.5rem !important;
	}
}

html.scroll-dir-down {
	.BookingWidget {
		.SwitchTabContent {
			&.sticked {
				top: 1.5px !important;
			}
		}
	}
}
</style>


<style scoped lang="scss">
@use 'sass:color';
@use 'sass:meta';
@use '~/styles/partials/_var.scss';
@use '~/styles/partials/_mixins.scss';

.BookingWidget {
	--tab-width: 244px;
	--tab-count: 6; // will be overwritten by 'style' attr
	--bluryBgElementWidthReduction: 0.985;
	
	:deep(.SwitchTabHeader .swiper-wrapper) {
		position: relative;
		
		&::before {
			content: '';
			backdrop-filter: blur(20px);
			@apply bg-neutral-grey-extralight bg-opacity-40;
			position: absolute;
			// 205px * (6 tabs) = 1230px
			// - 10% from it. This is arbitrary, it comes from trial & error.
			/* width: calc((var(--tab-width) * var(--tab-count)) * 0.9); */
			width: calc((var(--tab-width) * var(--tab-count)) * var(--bluryBgElementWidthReduction));
			// left 25px is arbitrary, it comes from trial & error.
			left: 25px;
			height: 100%;
			transform-origin: top left;
			transform: skew(-0.075turn);
			border-top-left-radius: 14px;
			border-top-right-radius: 14px;

			html[dir="rtl"] & {
				transform: skew(0.075turn);
				left: auto;
				right: 25px;
			}
		}
	}

	&.glass-design {
		margin-bottom: 0px !important;
	
	@media #{var.$query-min-2xl} {
		:deep(.swiper){
			:hover{
				background-color: #ffffff;
				color: var(--primary-blue-light);
			}
		}
		:deep(.SwitchTabHeaderItem){
			:hover{
				text-decoration-line: underline;
				text-decoration-style: solid;
			}
		}
		:deep(.SwitchTabHeaderItem.selected){
			:hover{
				text-decoration-style: solid;
				text-decoration-line: none;
				background-color: var(--primary-blue-extralight)!important;
			}
		}
	}
	@media #{var.$query-max-2xl} {
		:deep(.swiper){
			:hover{
				background-color: none;
				color: var(--primary-white-light);
			}
		}
	}
	
		:deep(.SwitchTabHeader .swiper-wrapper) {
			&::before {
				content: none;
			}
		}
	
		:deep(.SwitchTabHeaderItem) {
			min-width: 200px;

			&.selected {
				@media #{var.$query-min-xl}{
				.SwitchTabHeaderItemCenter {
					background-color: var(--primary-blue-extralight)!important;
					color: var(--primary-blue-light);
					box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.24);
					height: 37px;
					margin: 0 2px;
				}
				}
				@media #{var.$query-min-md}{
					.SwitchTabHeaderItemCenter {
						background-color: var(--primary-blue-extralight)!important;
						color: var(--primary-blue-light);
						box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.24);
						height: 32px; //height inside
						margin: 0 2px;
					}
				}
			}
		}
		:deep(.SwitchTabHeader .swiper) {
			@include mixins.glassTabContainer;
		}
		.tab-swiper :deep(.SwitchTabContent) {
			margin-top: 25px;
			@include mixins.glassTabContainer;
		}

		.glass-in-mobile {
			margin-top: -27px;

			:deep(.SwitchTabHeaderItem) {
				--tab-width: 120px;
				min-width: 138px;
			}

			:deep(.SwitchTabHeader) {
				.swiper {
					width: calc(100%);
					background: linear-gradient(
						90deg,
						rgba(255, 255, 255, 0.8) 0%,
						rgba(240, 243, 245, 0.8) 100%
					);

					.selected {
						.SwitchTabHeaderItemCenter {
							background-color: var(--primary-blue-base)!important;
							color: var(--neutral-white-base);
							margin: -3px 11px -1px 0px;
						}
					}
					.SwitchTabHeaderItemCenter {
						color: var(--primary-blue-base);
					}

					&:hover{
						// background-color: #ffffff;
						// color: var(--primary-blue-light);
					}
				}

				.SwitchTabArrow {
					// background: transparent;
					//background: linear-gradient(270deg, #1C1C1C 27.5%, rgba(28, 28, 28, 0.00) 58.75%);
					svg {
						fill: var(--primary-blue-base);
					}
				}
			}

			&.tab-swiper {
				:deep(.SwitchTabContent) {
					margin-top: 10px !important;
					border: 0px;
				}
			}
		}
	}

	:deep(.SwitchTabHeader .swiper) {
		width: calc(100% - 100px);
		max-width: calc((var(--tab-width) * var(--tab-count)) * 0.92);
		position: relative;
	}
	:deep(.SwitchTabHeader .SwitchTabArrow-wrapper) {
		background-color: transparent;
	}
	:deep(.SwitchTabHeader .SwitchTabArrow) {
		@apply bg-neutral-grey-extralight rounded-full;
	}

	:deep(.SwitchTabHeader) {
		.swiper-slide {

			&:first-child {
				html[dir="rtl"] & {
					margin-right: 0;
				}
			}

			&:last-child {
				html[dir="rtl"] & {
					margin-right: -20px;
				}
			}
		}
		
	}
	
	
	:deep(.SwitchTabHeaderItem) {
		width: var(--tab-width);
	}
	:deep(.SwitchTabHeaderItemLeft),
	:deep(.SwitchTabHeaderItemCenter),
	:deep(.SwitchTabHeaderItemRight) {
		background-color: transparent;
	}
	:deep(.SwitchTabHeaderItemLeft), :deep(.SwitchTabHeaderItemRight) {
		background-color: transparent;
		
		> svg {
			fill: transparent;
		}
	}
	
	:deep(.SwitchTabHeaderItem.selected) {
		@apply hover:text-neutral-blue-base;
		.SwitchTabHeaderItemCenter {
			background-color: white;
			color: var(--primary-blue-base);
		}
		.SwitchTabHeaderItemLeft, .SwitchTabHeaderItemCenter, .SwitchTabHeaderItemRight {
			--tw-bg-opacity: 1;
		}
		.SwitchTabHeaderItemLeft, .SwitchTabHeaderItemRight {
			background-color: transparent;
			
			> svg {
				fill: white;
			}
		}
	}

	:deep(.SwitchTabContent) {
		margin-top: -2px;
		z-index: 10000;
	}

}

@supports selector(.parent:has(.child)) {
	// see <SwitchTab> style section
	.BookingWidget {
		--tab-width: 205px;
		--bluryBgElementWidthReduction: 0.9;

		&.glass-design {
			--tab-width: 200px;

			@media #{var.$query-max-sm} {
				--tab-width: 180px;
			}

			:deep(.SwitchTabHeader) {
				.swiper {
					// max-width: calc(var(--tab-width) * var(--tab-count) * 0.95) !important;
					color: var(--neutral-white-base);
				}
			}
		}
	}
}

.mobile-swiper {
	@apply pb-6;
	
	&.is-locked {
		:deep(.swiper-wrapper) {
			justify-content: center;
		}
	}
	
	:deep(.swiper-slide:first-child) {
		@apply pl-4;
		
		html[dir="rtl"] & {
			@apply pl-0 pr-4;
		}
	}
	:deep(.swiper-slide:last-child) {
		@apply pr-4;
		
		html[dir="rtl"] & {
			@apply pr-0 pl-4;
		}
	}
	:deep(.swiper-scrollbar) {
		width: 80px;
		margin-left: auto;
		margin-right: auto;
		left: 0;
		right: 0;
		
		.swiper-scrollbar-drag {
			background-color: var(--primary-blue-base);
		}
	}
}

.mobile-item {
	:deep(svg) {
		width: 100%;
	}
}

:deep(.pin-spacer) {
	max-height: none !important;
	// height: auto !important;
	height: var(--pinSpacingHeight, auto) !important;
}
.tab-swiper :deep(.SwitchTabContent) {
	transition: top 0.2s;
	max-height: none !important;
	height: auto !important;
	width: 100%;
	@apply mx-auto px-4;

	@media #{var.$query-min-md} { max-width: #{var.$breakpoint-md}px; }
	@media #{var.$query-min-lg} { max-width: #{var.$breakpoint-lg}px; }
	@media #{var.$query-min-xl} { max-width: #{var.$breakpoint-xl}px; }
	@media #{var.$query-min-2xl} { max-width: #{var.$breakpoint-2xl}px; }
	
	&.sticked {
		max-height: none !important;
		height: auto !important;
		border-radius: 0;
		border-bottom: 2px solid var(--neutral-grey-light);
		top: calc(
			var(--mobileDlAppNotificationHeight) +
			var(--headerNotificationHeight) +
			var(--headerHeight)
		) !important;
		padding-bottom: 24px !important; // <-- pb-6
		box-shadow: none;
		padding-top: 24px !important;

		@media #{var.$query-min-md} {
			width: 100% !important;
			max-width: 100% !important;
			left: 0 !important;
			right: 0 !important;
		}
		
		.switch-tab-content-wrapper {
			display: none !important;
			/* max-height: calc(80vh - var(--headerNotificationHeight) - var(--headerHeight));
			overflow-y: auto; */
		}
		.slide-fade-enter-active,
		.slide-fade-leave-active {
			transition: none !important;
		}
		[data-tab-content-name="flight-search-tab"] {
			display: block !important;
		}
		
		.FlightSearch .tab-panels-container {
			
			> [identifier="multi-city"] {
				max-height: 70vh !important;
				overflow-y: auto !important;
				overscroll-behavior: none;
				@include mixins.styled-scrollbar();
				@apply pr-4;
			}
		}
	}
	&.pre-stick {
		visibility: hidden;
		opacity: 0;
	}
}

</style>
